import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

export interface RegulatoryCautionItem {
  symbol: string;
  name?: string;
  messages: string[];
}

export interface RegulatoryDialogData {
  items: RegulatoryCautionItem[];
  noteText?: string;
}

/**
 * Displays regulatory caution messages for symbols under regulation.
 * 
 * Purpose: Shows a list of symbols with their associated regulatory warnings/messages
 * and requires explicit user acceptance before proceeding with deployment/resumption.
 * 
 * Used before:
 * - Deploying strategies with regulated symbols
 * - Resuming paused/stopped portfolios containing regulated symbols
 * - Carry-forward resume operations for regulated portfolios
 * 
 * Features:
 * - Displays symbol chips with regulatory status
 * - Lists all regulatory messages per symbol
 * - Requires explicit acceptance via "Accept" button
 * - Allows cancellation via close icon or rejection
 * 
 * @example
 * this.dialog.open(RegulatoryDialogComponent, {
 *   width: '600px',
 *   panelClass: 'ut-pro-dialog-box-custom-header',
 *   data: {
 *     items: [{
 *       symbol: 'XYZ',
 *       name: 'XYZ Corporation',
 *       messages: ['This stock is under regulatory surveillance']
 *     }],
 *     noteText: 'Custom disclaimer text (optional)'
 *   }
 * }).afterClosed().subscribe(accepted => {
 *   if (accepted) {
 *     // Proceed with operation
 *   }
 * });
 */
@Component({
  selector: 'app-regulatory-dialog',
  templateUrl: './regulatory-dialog.component.html',
  styleUrls: ['./regulatory-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegulatoryDialogComponent {
  public items: RegulatoryCautionItem[] = [];
  public noteText: string = 'Please analyze the risks carefully before proceeding. Click on Accept button to place the order.';

  constructor(
    private dialogRef: MatDialogRef<RegulatoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RegulatoryDialogData | null,
  ) {
    this.items = data?.items ?? [];
    this.noteText = data?.noteText ?? this.noteText;
  }

  public close(): void {
    this.dialogRef.close(false);
  }

  public accept(): void {
    this.dialogRef.close(true);
  }

  /**
   * TrackBy function for *ngFor over items array.
   * Improves performance by tracking items by symbol.
   */
  public trackBySymbol(index: number, item: RegulatoryCautionItem): string {
    return item.symbol;
  }

  /**
   * TrackBy function for *ngFor over messages array.
   * Tracks by index since messages are strings without unique identifiers.
   */
  public trackByMessage(index: number, message: string): number {
    return index;
  }
}
